import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { concatAndDeDuplicateObjects } from '@/lib/utils';

interface PhotoItem {
  id?: string;
  local: string;
  tag: string;
  task_id: string;
  job_id: string;
  asset_id: string;
  temp_id: string;
  uri: string;
  originalUri?: string;
  originalLocal?: string;
  original_asset_id?: string;
  // Offline storage metadata (all serializable)
  cached?: boolean;
  fileSize?: number;
  fileType?: string;
  storedAt?: string;
  // Upload status tracking
  status?: 'pending' | 'uploading' | 'completed' | 'error';
  progress?: number;
  error?: string;
  [key: string]: any;
}

interface PhotosState {
  items: PhotoItem[];
  loading: boolean;
  error: string | null;
}

const initialState: PhotosState = {
  items: [],
  loading: false,
  error: null,
};

const photosSlice = createSlice({
  name: 'photos',
  initialState,
  reducers: {
    // Action creators for Redux Offline
    unlinkPhoto: (state, action: PayloadAction<{ temp_id: string }>) => {
      state.items = state.items.filter(photo => photo.temp_id !== action.payload.temp_id);
    },

    // Update photo upload status and progress
    updatePhotoStatus: (state, action: PayloadAction<{
      temp_id: string;
      status: 'pending' | 'uploading' | 'completed' | 'error';
      progress?: number;
      error?: string;
    }>) => {
      const photo = state.items.find(p => p.temp_id === action.payload.temp_id);
      if (photo) {
        photo.status = action.payload.status;
        if (action.payload.progress !== undefined) {
          photo.progress = action.payload.progress;
        }
        if (action.payload.error) {
          photo.error = action.payload.error;
        }

        // Clear cache when upload completed successfully
        if (action.payload.status === 'completed') {
          photo.cached = false;
          // Clear object URL to prevent memory leaks
          if (photo.uri && photo.uri.startsWith('blob:')) {
            URL.revokeObjectURL(photo.uri);
            photo.uri = '';
          }
          // Note: Cache cleanup happens in the upload action itself
          console.log('✅ Photo upload completed, cache will be cleaned up:', photo.temp_id);
        }
      }
    },

    // Clean up cached photo after successful upload (legacy)
    cleanupCachedPhoto: (state, action: PayloadAction<{ temp_id: string }>) => {
      const photo = state.items.find(p => p.temp_id === action.payload.temp_id);
      if (photo) {
        photo.cached = false;
        photo.uri = ''; // Clear object URL to prevent memory leaks
      }
    },

    linkPhoto: (state, action: PayloadAction<PhotoItem>) => {
      // If photo already exists, do not add it again
      const existingPhoto = state.items.find(
        photo => photo.local === action.payload.local && photo.tag === action.payload.tag
      );
      
      if (!existingPhoto) {
        state.items.unshift(action.payload);
      }
    },
    linkPhotoCommit: (state, action: PayloadAction<any>) => {
      // Handle success - cleanup cached photo after successful upload
      const payload = action.payload;
      if (payload && payload.temp_id) {
        const photo = state.items.find(p => p.temp_id === payload.temp_id);
        if (photo && photo.cached) {
          // Mark as successfully uploaded and no longer cached locally
          photo.cached = false;
          // Clean up object URL to prevent memory leaks
          if (photo.uri && photo.uri.startsWith('blob:')) {
            URL.revokeObjectURL(photo.uri);
            photo.uri = '';
          }
        }
      }
    },
    linkPhotoFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Handle queued upload actions
    queuePhotoUpload: (state, action: PayloadAction<{ temp_id: string }>) => {
      // Mark photo as queued for upload
      const photo = state.items.find(p => p.temp_id === action.payload.temp_id);
      if (photo) {
        photo.status = 'pending';
        photo.progress = 0;
      }
    },
    queuePhotoUploadCommit: (state, action: PayloadAction<any>) => {
      // Handle successful upload - update with server data
      const serverResponse = action.payload;
      const { meta } = action as any;

      console.log('📤 Upload completed, server response:', serverResponse);
      console.log('📤 Upload meta:', meta);

      if (meta && meta.temp_id) {
        const photo = state.items.find(p => p.temp_id === meta.temp_id);
        if (photo) {
          photo.status = 'completed';
          photo.progress = 100;
          photo.cached = false;

          // Update with server response data for remote URL building
          if (serverResponse) {
            photo.filename = serverResponse.filename || photo.filename;
            photo.id = serverResponse.id || photo.id;
            photo.asset_id = serverResponse.asset_id || photo.asset_id;
            // Don't overwrite job_id, task_id as they should already be correct

            console.log('✅ Photo updated with server data:', {
              temp_id: photo.temp_id,
              filename: photo.filename,
              job_id: photo.job_id
            });
          }
        }
      }
    },
    queuePhotoUploadRollback: (state, action: PayloadAction<any>) => {
      // Handle failed upload
      const { meta } = action as any;
      if (meta && meta.temp_id) {
        const photo = state.items.find(p => p.temp_id === meta.temp_id);
        if (photo) {
          photo.status = 'error';
          photo.error = 'Upload failed';
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Handle data from GET_BOOKINGS_COMMIT
    builder.addCase('GET_BOOKINGS_COMMIT', (state, action: PayloadAction<{
      photos?: PhotoItem[];
      bookings?: any[];
      [key: string]: any;
    }>) => {
      const { photos } = action.payload;
      const { meta } = action as any;

      const isPagedRequest = meta && meta.page !== undefined && meta.page > 0;

      // Transform server photos to match PhotoItem interface
      const transformedPhotos = photos?.map((serverPhoto: any) => ({
        // Server properties
        id: serverPhoto.id,
        local: serverPhoto.local,
        tag: serverPhoto.tag,
        task_id: serverPhoto.task_id,
        job_id: serverPhoto.job_id,
        asset_id: serverPhoto.asset_id,
        temp_id: serverPhoto.temp_id || serverPhoto.asset_id, // Use asset_id as temp_id if temp_id is missing

        // Client properties (with defaults)
        uri: serverPhoto.uri || undefined, // Server photos typically don't have URI
        originalUri: serverPhoto.originalUri || undefined,
        originalLocal: serverPhoto.originalLocal || undefined,
        original_asset_id: serverPhoto.original_asset_id,

        // Offline storage metadata (defaults for server photos)
        cached: false, // Server photos are not cached locally by default
        fileSize: serverPhoto.size ? serverPhoto.size * 1024 : undefined, // Convert KB to bytes if available
        fileType: serverPhoto.mime || undefined,
        storedAt: serverPhoto.created_at || undefined,

        // Upload status tracking (server photos are already uploaded)
        status: 'completed' as const,
        progress: 100,
        error: undefined,

        // Pass through any additional properties
        ...serverPhoto
      })) || [];

      if (isPagedRequest) {
        if (transformedPhotos && transformedPhotos.length > 0) {
          state.items = concatAndDeDuplicateObjects('id', state.items, transformedPhotos);
        }
      } else {
        // First page load or refresh - replace all photos
        state.items = transformedPhotos;
      }
    });
  },
});

export const {
  unlinkPhoto,
  updatePhotoStatus,
  cleanupCachedPhoto,
  linkPhoto,
  linkPhotoCommit,
  linkPhotoFailed,
  queuePhotoUpload,
  queuePhotoUploadCommit,
  queuePhotoUploadRollback,
} = photosSlice.actions;

export default photosSlice.reducer;
